<template>
	<div :ref="fieldRef" class="display-flex flex-column">
		<div class="connection-group-container display-flex flex-row width-100 flex-align-center">
			<ConnectionServiceCard :serviceSlug="serviceSlug" />
			<div class="display-flex flex-row flex-align-center flex-justify-space-between width-100">
				<ConnectionServiceAndActionsDescription
					:serviceTitle="serviceTitle"
					:numOfActions="serviceRelatedActions.length"
					:isShowingActionsDetails="isShowingActionsDetails"
					@see-actions-details="isShowingActionsDetails = true"
					@close-actions-details="isShowingActionsDetails = false"
				/>
				<ProfileConnectionStatus
					v-if="!narrowLayout"
					:profileId="value"
					:editMode="editMode"
					:serviceId="selectedApp.id"
					:isLoadingProfile="isLoadingProfile"
					:isSelectingProfile="isSelectingProfile"
					@reconnect-profile="forceReconnectProfile"
					@open-profile-selection="onOpenProfileSelection"
					@close-profile-selection="isSelectingProfile = false"
				/>
			</div>
		</div>
		<ProfileConnectionStatus
			v-if="narrowLayout"
			:profileId="value"
			:editMode="editMode"
			:alignStart="narrowLayout"
			:serviceId="selectedApp.id"
			:isLoadingProfile="isLoadingProfile"
			:isSelectingProfile="isSelectingProfile"
			@reconnect-profile="forceReconnectProfile"
			@open-profile-selection="onOpenProfileSelection"
			@close-profile-selection="isSelectingProfile = false"
		/>
		<v-expand-transition>
			<ActionsDetailsMenu class="actions-menu-container" :actions="field.source_of_nodes || []" v-if="isShowingActionsDetails" />
		</v-expand-transition>
		<v-expand-transition>
			<ProfileSelector
				ref="profile_selector"
				:profileId="value"
				:hideExplanation="true"
				v-show="isSelectingProfile"
				:serviceId="selectedApp.id"
				@select-profile="setProfile"
				:serviceVersionId="selectedAppVersion"
				@set-testing-profile="setTestingProfile"
				@profile-set-successful="onProfileSelection"
				:customFieldProps="activeFormFieldCommonProps"
				:includeProjectRelatedConnections="includeProjectRelatedConnections"
				@profile-set-failed="isConnectionFailed = true"
			/>
		</v-expand-transition>
	</div>
</template>

<script>
import { mapGetters } from 'vuex';
import AppService from '@/utils/AppService';
import WidgetFieldMixin from './WidgetFieldMixin';
import IsRequiredFieldComputedMixin from './mixins/IsRequiredFieldComputedMixin';
import { getService, getCredentialsForService } from '@/store/modules/apps/getters';
import ActionsDetailsMenu from '@/views/Flow/configurations/SetupMenu/CredentialsSelector/ActionsDetailsMenu.vue';
import ConnectionServiceCard from '@/views/Flow/configurations/SetupMenu/CredentialsSelector/ConnectionServiceCard';
import ProfileConnectionStatus from '@/views/Flow/configurations/SetupMenu/CredentialsSelector/ProfileConnectionStatus';
import ConnectionServiceAndActionsDescription from '@/views/Flow/configurations/SetupMenu/CredentialsSelector/ConnectionServiceAndActionsDescription';

export default {
	name: 'CredentialsSelectorField',
	props: {
		editMode: { type: Boolean, default: false },
		narrowLayout: { type: Boolean, default: false },
		ignoreRequired: { type: Boolean, default: false },
		hideExplanation: { type: Boolean, default: false },
		isGeneralConfiguration: { type: Boolean, default: false },
	},
	mixins: [WidgetFieldMixin, IsRequiredFieldComputedMixin],
	components: {
		ActionsDetailsMenu,
		ConnectionServiceCard,
		ProfileConnectionStatus,
		ConnectionServiceAndActionsDescription,
		ProfileSelector: () => import('@/components/common/ProfileSelector'),
	},
	methods: {
		doesRequiresAuth(app) {
			return AppService.doesRequiresAuth(app);
		},
		setProfile(p) {
			this.value = p;
		},
		setTestingProfile(newV) {
			this.isLoadingProfile = newV;
		},
		forceReconnectProfile() {
			if (this.value && this.selectedProfile) {
				this.$refs.profile_selector?.forceReconnectProfile(this.value, true);
			}
		},
		onProfileSelection() {
			this.isConnectionFailed = false;
			this.isSelectingProfile = false;
		},
		onOpenProfileSelection() {
			const profileSelectorRef = this.$refs.profile_selector;
			if (this.profileItemsList.length === 0 && profileSelectorRef) {
				profileSelectorRef.forceAddProfile();
			} else {
				this.isSelectingProfile = true;
			}
		},
	},
	computed: {
		...mapGetters([getService.fullPath, getCredentialsForService.fullPath]),
		profileItemsList() {
			return this[getCredentialsForService.fullPath](this.selectedApp.id) || [];
		},
		serviceSlug() {
			return this.widgetFieldMetadataSource.service_slug || this.selectedApp.service_slug;
		},
		serviceTitle() {
			const additionalTitleObj = this.field.metadata.options.additional_title || {};
			const additionalTitle = additionalTitleObj.enabled && additionalTitleObj.value ? ' - ' + additionalTitleObj.value : '';
			return (this.widgetFieldMetadataSource.service_name || this.selectedApp.service_name || '') + additionalTitle;
		},
		serviceRelatedActions() {
			return this.field.source_of_nodes || [];
		},
		widgetFieldMetadataSource() {
			return this.field.metadata.source || {};
		},
		selectedApp() {
			return this[getService.fullPath]([this.field.metadata.source.service]) || {};
		},
		selectedAppVersion() {
			return this.field.metadata.source.service_version;
		},
		selectedProfile() {
			if (!this.value) {
				return null;
			}
			return this.profileItemsList.find((x) => x.id === this.value);
		},
		includeProjectRelatedConnections() {
			// if user configuration show only private connections, if general show all types of connections
			return this.isGeneralConfiguration;
		},
	},
	data() {
		return {
			isLoadingProfile: false,
			isSelectingProfile: false,
			isShowingActionsDetails: false,
		};
	},
	watch: {
		isShowingActionsDetails(newV) {
			if (newV && this.isSelectingProfile) {
				this.isSelectingProfile = false;
			}
		},
		isSelectingProfile(newV) {
			if (newV && this.isShowingActionsDetails) {
				this.isShowingActionsDetails = false;
			}
		},
	},
	mounted() {
		this.$nextTick(() => {
			this.forceReconnectProfile();
		});
	},
};
</script>

<style lang="scss" scoped>
.connection-group-container {
	height: 67px;
}
.actions-menu-container {
	padding-left: 50px;
}
</style>
